// Sentry triage v1.25.0 Group 3 — deferred-login "stuck offline" decision logic.
//
// When couch login fails on a flaky / offline network, the app proceeds in offline
// mode and defers the login to a focus/online auto-retry (plus a splash Retry
// button). That is the expected, recoverable path — so the deferral itself is NOT
// reported as an error. But a user who boots offline and stays stuck long enough
// will just bail, and we want to hear about that. This pure function decides whether
// a still-deferred login should be reported as a one-shot "stuck" signal.
//
// Pure module (no Angular / Sentry deps) so it runs under node for unit tests —
// same pattern as app/splash-watchdog.ts and services/database-wrapper-passthrough.ts.
// auth.service.ts holds the state and side effects; this holds only the decision.

/** Deferred login still not recovered after this long (while foreground) → report stuck. */
export const STUCK_DEFERRED_LOGIN_MS = 10_000;

/** What prompted a stuck evaluation. `timer` = the boot watchdog fired; `auto-retry` =
 * a focus/online auto-retry ran and failed; `foreground` = the app returned to the
 * foreground while still offline (re-check, since the boot timer may have fired and been
 * skipped while backgrounded); `manual-retry` = the user tapped the splash Retry button. */
export type StuckEvalTrigger = 'timer' | 'auto-retry' | 'foreground' | 'manual-retry';

export interface CouchLoginStuckState {
  /** Is a deferred couch login still pending (not yet recovered)? */
  retryStillNeeded: boolean;
  /** Milliseconds the user has been waiting since the login was first deferred. */
  deferredElapsedMs: number;
  /** Is the app currently in the foreground (user actively waiting)? */
  foreground: boolean;
  /** Is the browser currently online? */
  online: boolean;
  /** Has the stuck signal already been emitted for this deferred-login episode? */
  alreadyReported: boolean;
  /** What prompted this evaluation. */
  trigger: StuckEvalTrigger;
}

export interface CouchLoginStuckDecision {
  /** Emit the one-shot "stuck offline" telemetry. */
  report: boolean;
}

export function evaluateCouchLoginStuck(state: CouchLoginStuckState): CouchLoginStuckDecision {
  // nothing to report once the login recovered, or if we already reported this episode
  if (!state.retryStillNeeded || state.alreadyReported) {
    return { report: false };
  }

  // a user-initiated Retry that fails while online is the clearest "about to bail"
  // signal — report immediately, with no wait threshold. (offline manual taps are
  // covered by the timer path instead, so a user with no connection isn't reported
  // as an online auth failure.)
  if (state.trigger === 'manual-retry') {
    return { report: state.online };
  }

  // passive paths (boot timer, a focus/online auto-retry that kept failing, or a
  // foreground re-check while offline): only report once the user has plausibly been
  // kept waiting — foreground and past the wait threshold
  const waitedLongEnough = state.deferredElapsedMs >= STUCK_DEFERRED_LOGIN_MS;
  return { report: state.foreground && waitedLongEnough };
}
